'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { bg0 } from '@osmosis/tailwind-ui/styles/background';
import {
  AIPanelProvider,
  AIPanelOptions,
  useAIPanelContext,
  getPanelOptions,
  AIPanelPage,
  PANEL_MIN_WIDTH,
} from '../../context/AIPanelContext';
import AIPanel from './AIPanel';
import PanelResizer from './PanelResizer';
import MobileSectionNav from './mobile/MobileSectionNav';
import MobileOverlayHeader from './mobile/MobileOverlayHeader';
import { buildMobileSections, SectionId } from './mobile/sections';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import {
  ContentSizeProvider,
  useContentSize,
} from '@osmosis/nextjs-components/context/ContentSizeContext';
import { Answer } from '@osmosis/shared/types/pages/answerPages';
import { getContextFromAnswers } from '../../lib/util/oai';
import {
  AIPanelHeightSyncProvider,
  useAIPanelHeightSync,
} from '../../context/AIPanelHeightSyncContext';
import type { TrialCtaArgs } from '../../lib/composer/schemas/answersValueProp';

/** Min width of the resizable right rail (px). */
const RAIL_MIN_WIDTH = 280;

/**
 * Height of the fixed app-shell layout: the viewport minus the global header
 * (48px) and the B2C sale banner when it is visible. `SaleBanner` publishes its
 * measured height to `--os-sale-banner-height` (0px when absent), so the shell
 * shrinks to fit above the fold instead of overflowing by the banner height.
 * Used by {@link DefaultPanelLayout} to fix the whole shell to viewport
 * height. The sectioned (learn v2) layout scrolls the whole document instead —
 * except the rail still falls back to this bound when the AI panel isn't
 * sized from the measured video height, so its `min-h-[%]`/`flex-1` sizing
 * (and the AI panel's own internal chat scroll) has something to size against.
 */
const APP_SHELL_HEIGHT =
  'h-[calc(100vh-48px-var(--os-sale-banner-height,0px))]';

/** Shared outer shell: full-width padded column on the page background. */
const LAYOUT_SHELL_CLASS =
  'flex w-full flex-col overflow-x-clip bg-neutral-100 px-4 pt-4';

/** Inline style for the resizable right rail, shared by both layouts. */
function buildRailStyle(width: number): React.CSSProperties {
  return {
    width,
    minWidth: RAIL_MIN_WIDTH,
    flexShrink: 0,
    transition: 'width ease',
  };
}

function buildAiSectionMdClass({
  isOpen,
  matchAIHeight,
  isLoggedOut,
}: {
  isOpen: boolean;
  matchAIHeight: boolean;
  isLoggedOut: boolean;
}): string {
  const commonClass = 'block w-full';
  if (!isOpen) return cn(commonClass, 'shrink-0');
  if (matchAIHeight) return commonClass;
  return cn(
    commonClass,
    isLoggedOut ? '' : 'h-[1000px] min-h-[60vh] max-h-[60vh]',
  );
}

type PanelContentProps = {
  contentRef: React.RefObject<HTMLDivElement | null>;
  className?: string;
  children: React.ReactNode;
};

/**
 * Content column shared by both layouts: binds the {@link ContentSizeProvider}
 * to the content container and renders the `#ai-panel-content` scroll area.
 */
const PanelContent = ({
  contentRef,
  className,
  children,
}: PanelContentProps) => (
  <ContentSizeProvider containerRef={contentRef}>
    <Box
      ref={contentRef}
      id="ai-panel-content"
      className={cn('min-w-0 flex-1 rounded-lg', bg0, className)}
    >
      {children}
    </Box>
  </ContentSizeProvider>
);

type Props = {
  children: React.ReactNode;
  panelOptions: AIPanelOptions;
  title?: string;
  pageContext?: string;
  answers?: Answer[];
  showExamplePrompts?: boolean;
  showLoggedOut?: boolean;
  /** Answer-page-only shared signup CTA copy for logged-out panel content. */
  loggedOutTrialCta?: TrialCtaArgs;
  classNames?: {
    container?: string;
    content?: string;
    panel?: string;
  };
  /**
   * Optional content rendered in the right rail, below the AI panel, such as an
   * "Up next" / related-content sidenav.
   */
  sidebarFooter?: React.ReactNode;
  /**
   * Keeps the right rail visible even when the AI panel is closed. Closing the
   * panel then just collapses the AI section, leaving `sidebarFooter` in place.
   * Only meaningful together with `sidebarFooter`. (Desktop / md+ only.).
   */
  keepSidebarOpen?: boolean;
  hideSidebar?: boolean;
  /**
   * When true, the expanded AI panel is sized to match the height of the
   * content element registered via {@link AIPanelHeightSyncProvider} (the Learn
   * page video "feature": header + player) instead of a fixed `min-h`. Desktop
   * (md+) and open-panel only; falls back to the default sizing otherwise.
   */
  matchAIHeightToContent?: boolean;
};

type WithAIPanelInnerProps = Pick<
  Props,
  | 'classNames'
  | 'sidebarFooter'
  | 'keepSidebarOpen'
  | 'loggedOutTrialCta'
  | 'hideSidebar'
> & {
  children: React.ReactNode;
};

type DefaultPanelLayoutProps = Pick<
  Props,
  'classNames' | 'loggedOutTrialCta' | 'hideSidebar'
> & {
  children: React.ReactNode;
};

/**
 * Default layout shell — content + AI panel side by side. Used by every page
 * that does not opt into the mobile sectioned-panel layout (which has its own
 * mobile-aware layout below).
 */
const DefaultPanelLayout = ({
  children,
  classNames = {},
  loggedOutTrialCta,
  hideSidebar = false,
}: DefaultPanelLayoutProps) => {
  const { isOpen, panelWidth, shouldRenderAIChat } = useAIPanelContext();
  const { md } = useContentSize(true);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasBeenOpen, setHasBeenOpen] = useState(isOpen);

  // Once the panel has opened, keep the desktop shell and content scroll area
  // in place when it closes. This prevents the page from switching back to
  // document scrolling and moving the user's viewport.
  useEffect(() => {
    if (isOpen) setHasBeenOpen(true);
  }, [isOpen]);

  const keepsDesktopScrollLayout = hasBeenOpen && md;
  const hasFixedShell = isOpen || keepsDesktopScrollLayout;

  const railStyle = buildRailStyle(panelWidth);
  return (
    <Box
      className={cn(
        LAYOUT_SHELL_CLASS,
        hasFixedShell ? APP_SHELL_HEIGHT : '',
        classNames?.container,
      )}
    >
      <Flex
        className={cn(
          'min-h-0 w-full flex-1',
          hasFixedShell ? 'overflow-x-clip' : '',
        )}
      >
        {/* Content area — hidden on mobile when panel is open */}
        <PanelContent
          contentRef={contentRef}
          className={cn(
            isOpen
              ? 'hidden md:block md:overflow-y-auto'
              : keepsDesktopScrollLayout
                ? 'overflow-y-auto'
                : 'overflow-hidden',
            classNames?.content,
          )}
        >
          {children}
        </PanelContent>

        {/* Resizer sits between content and panel — only while the panel is open */}
        {!hideSidebar && isOpen && <PanelResizer />}
        {!hideSidebar && (
          <Flex
            id="ai-panel-sidebar"
            style={railStyle}
            className={cn(
              'overflow-hidden rounded-lg',
              // Below md the content column is hidden, so let the rail fill the
              // screen instead of staying capped at the inline `panelWidth`.
              // `!` overrides the inline width/min-width; `max-md:` keeps desktop
              // (resizable width) untouched.
              'max-md:!w-full max-md:!min-w-0',
              isOpen ? 'flex h-full w-full max-md:!w-full flex-col' : 'hidden',
              classNames?.panel,
              shouldRenderAIChat && 'gap-3',
            )}
          >
            <AIPanel trialCta={loggedOutTrialCta} />
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

type SectionedPanelLayoutBodyProps = WithAIPanelInnerProps & {
  layoutRef: React.RefObject<HTMLDivElement | null>;
};

/**
 * Body of the mobile sectioned-panel layout. Consumes the outer
 * ContentSizeProvider so the desktop rail <-> mobile bottom-nav switch is
 * driven by `getClassValue` (className-only). Because the AI panel (OAI iframe)
 * and the sidebar content are rendered once and only repositioned via classes,
 * neither the iframe nor the link list ever remounts across resize or section
 * open/close.
 */
const SectionedPanelLayoutBody = ({
  children,
  classNames = {},
  sidebarFooter,
  keepSidebarOpen = false,
  loggedOutTrialCta,
  layoutRef,
}: SectionedPanelLayoutBodyProps) => {
  const { formatMessage } = useIntl();
  const {
    isOpen,
    panelWidth,
    shouldRenderAIChat,
    isLoggedOut,
    openPanel,
    closePanel,
  } = useAIPanelContext();
  const { getClassValue, md, isReady } = useContentSize();
  const { matchedHeight, enabled: heightSyncEnabled } = useAIPanelHeightSync();
  const contentRef = useRef<HTMLDivElement>(null);

  // Wrappers for the section overlays, keyed by id, so focus can be moved into
  // whichever overlay is active on mobile.
  const relatedSectionRef = useRef<HTMLDivElement>(null);
  const aiSectionRef = useRef<HTMLDivElement>(null);
  const sectionRefs: Partial<
    Record<SectionId, React.RefObject<HTMLDivElement | null>>
  > = {
    related: relatedSectionRef,
    ai: aiSectionRef,
  };

  // The section whose overlay currently holds focus (or `null`). Lets the focus
  // effect tell whether focus still needs to move once the overlay is actually
  // visible, rather than relying on a single state transition.
  const focusedSectionRef = useRef<SectionId | null>(null);

  // Which full-screen section overlay is showing on mobile. `null` = bare
  // content + bottom nav. Ignored on desktop (overlay classes are md-gated).
  const [activeSection, setActiveSection] = useState<SectionId | null>(null);

  const hasRelated = !!sidebarFooter;

  const labels = useMemo(
    () => ({
      related: formatMessage({
        id: 'panel.section.related',
        defaultMessage: 'Related',
      }),
      ai: formatMessage({
        id: 'ai-panel.title',
        defaultMessage: 'Ask Osmosis AI',
      }),
      schedule: formatMessage({
        id: 'panel.section.schedule',
        defaultMessage: 'My Schedule',
      }),
    }),
    [formatMessage],
  );

  const available = useMemo(
    () => ({ related: hasRelated, ai: shouldRenderAIChat }),
    [hasRelated, shouldRenderAIChat],
  );

  const sections = useMemo(
    () => buildMobileSections({ labels, available }),
    [labels, available],
  );

  // Reflect the AI panel's open state as the active overlay on mobile. Covers
  // both initial load (the panel opens by default when eligible) and the
  // desktop->mobile transition, mirroring the desktop "open by default"
  // behavior. `prev ?? 'ai'` never overrides an explicit section selection: a
  // non-null `activeSection` only coexists with a closed panel, so this effect
  // doesn't fire then. Desktop is unaffected (overlay classes are md-gated).
  useEffect(() => {
    if (!md && isOpen && shouldRenderAIChat) {
      setActiveSection((prev) => prev ?? 'ai');
    }
  }, [md, isOpen, shouldRenderAIChat]);

  // The AI panel owns its own Hide button (it calls `closePanel`). On mobile,
  // hiding the chat should also close the overlay rather than leaving an empty
  // collapsed shell, so clear the active section once the panel is closed.
  useEffect(() => {
    if (activeSection === 'ai' && !isOpen) {
      setActiveSection(null);
    }
  }, [activeSection, isOpen]);

  const handleSelectSection = (id: SectionId) => {
    const next = activeSection === id ? null : id;
    setActiveSection(next);
    if (next === 'ai') {
      openPanel();
    } else if (activeSection === 'ai') {
      // Leaving an open AI overlay (closed, or switched to another section).
      // The focus effect returns focus to the bottom nav, so suppress the
      // panel's default focus restoration on mobile.
      closePanel(md ? undefined : { restoreFocus: false });
    }
  };

  // Closes whatever overlay is currently active (used by the in-overlay Hide
  // buttons). Mirrors the AI panel's own close so state stays consistent.
  const closeActiveSection = () => {
    if (activeSection === 'ai')
      closePanel(md ? undefined : { restoreFocus: false });
    setActiveSection(null);
  };

  // Moves focus to follow the active overlay on mobile (a11y). Focus only lands
  // once the layout has measured (`isReady`), because the overlay is rendered
  // `display:none` until then — so the AI panel's auto-open on page load (where
  // `isOpen` flips before measurement) is handled here rather than by AIPanel's
  // own focus effect. When the overlay opens, focus moves inside it; when it
  // closes, focus returns to its bottom-nav button. Desktop is exempt (no
  // overlays / bottom nav there).
  useEffect(() => {
    if (md || !isReady) return;
    const focused = focusedSectionRef.current;
    if (activeSection && activeSection !== focused) {
      sectionRefs[activeSection]?.current?.focus();
      focusedSectionRef.current = activeSection;
    } else if (!activeSection && focused) {
      const navButton = layoutRef.current?.querySelector<HTMLElement>(
        `[data-testid="mobile-section-nav-${focused}"]`,
      );
      navButton?.focus();
      focusedSectionRef.current = null;
    }
  }, [activeSection, md, isReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lock the page (body) scroll while a mobile overlay is open. The overlays are
  // fixed-position, so without this a scroll gesture inside one chains through to
  // the document and scrolls the page behind it. Desktop has no overlays, so it's
  // exempt; the previous body styles are restored on close/unmount.
  useEffect(() => {
    if (md || !activeSection) return;
    const { style } = document.body;
    const prevOverflow = style.overflow;
    const prevOverscroll = style.overscrollBehavior;
    style.overflow = 'hidden';
    style.overscrollBehavior = 'none';
    return () => {
      style.overflow = prevOverflow;
      style.overscrollBehavior = prevOverscroll;
    };
  }, [md, activeSection]);

  const railStyle = buildRailStyle(panelWidth || PANEL_MIN_WIDTH);

  // Content column: left column on desktop, full-width area with bottom-nav
  // clearance on mobile. `block` so the content child (which uses
  // `margin:auto` + max-width) fills and tracks the column width as the panel
  // is resized — a flex parent would instead center it at its content width.
  const contentClass = getClassValue({
    defaultClasses: 'block pb-16 md:pb-0',
    breakpoints: {
      md: 'block',
      base: 'block pb-16',
    },
  });
  const hasOnlyChat = shouldRenderAIChat && !hasRelated;

  // Whether the rail has anything to show at all. When neither the AI chat nor
  // the Related section is available, the rail (and the mobile bottom nav) are
  // omitted entirely and the content takes the full width.
  const hasAnySection = shouldRenderAIChat || hasRelated;

  const hideRailOnDesktop = hasOnlyChat && !isOpen;

  // When height-sync is on (Learn v2 desktop, panel open) the AI section is
  // sized to the measured video-feature height, so the
  // expanded chat lines up with the bottom of the video player.
  const matchAIHeight =
    heightSyncEnabled && md && isOpen && matchedHeight != null;

  // Rail: in-flow column on desktop; on mobile it generates no box so its
  // children can be positioned as independent overlays. Hidden (but mounted) on
  // desktop when the AI-only rail has been closed, so the iframe survives.
  const railClass = getClassValue({
    defaultClasses: 'hidden md:flex md:h-full md:w-full md:flex-col',
    breakpoints: {
      md: hideRailOnDesktop
        ? 'hidden'
        : cn(' md:h-full flex w-full flex-col', shouldRenderAIChat && 'gap-3'),
      base: 'contents',
    },
  });

  // Resizer: desktop only; also hidden when the AI-only rail is closed.
  const resizerWrapperClass = getClassValue({
    defaultClasses: 'hidden md:contents',
    breakpoints: {
      md: hideRailOnDesktop ? 'hidden' : 'contents',
      base: 'hidden',
    },
  });

  // Mobile overlays cover ~85% of the viewport, anchored above the bottom nav
  const mobileOverlay = cn(
    'fixed inset-x-0 bottom-16 top-[15%] z-20 shadow-[0px_0px_20px_20px_#00000080] rounded-lg',
    bg0,
  );

  // AI section wrapper. Desktop: upper part of the rail (or the full rail when
  // it's the only section). Mobile: ~85% overlay shown only when AI is the
  // active section.
  const aiSectionClass = getClassValue({
    defaultClasses: 'hidden w-full md:block',
    breakpoints: {
      md: buildAiSectionMdClass({
        isOpen,
        matchAIHeight,
        isLoggedOut,
      }),
      base:
        activeSection === 'ai' ? cn(mobileOverlay, 'flex flex-col') : 'hidden',
    },
  });

  const aiSectionStyle: React.CSSProperties | undefined = matchAIHeight
    ? { height: matchedHeight, flexShrink: 0 }
    : undefined;

  // Related section wrapper. Always mounted (crawler-discoverable links).
  // Desktop: lower part of the rail. Mobile: ~85% overlay when active,
  // otherwise hidden in the DOM (not unmounted).
  // min-h-1/2 doesn't work here so used arbitrary min-height value
  const relatedSectionClass = getClassValue({
    defaultClasses: cn(
      // eslint-disable-next-line @nx/workspace/tailwind-style-enforcement
      'hidden flex-1 flex-col rounded-lg md:flex',
      bg0,
    ),
    breakpoints: {
      // eslint-disable-next-line @nx/workspace/tailwind-style-enforcement
      md: cn('flex flex-1 flex-col rounded-lg', bg0),
      base:
        activeSection === 'related'
          ? cn(mobileOverlay, 'flex flex-col rounded-lg')
          : 'hidden',
    },
  });

  // Bottom nav: mobile only.
  const navClass = getClassValue({
    defaultClasses: 'flex md:hidden',
    breakpoints: { md: 'hidden', base: 'flex' },
  });

  // Related-content scroll box, inside the related section. Mobile keeps its
  // own scroll (it's the body of a fixed-position overlay); desktop/tablet
  // flows with the page instead of scrolling independently.
  const relatedContentClass = getClassValue({
    defaultClasses: 'min-h-0 flex-1 overflow-y-auto overscroll-contain',
    breakpoints: {
      md: 'min-h-0 flex-1',
      base: 'min-h-0 flex-1 overflow-y-auto overscroll-contain',
    },
  });

  return (
    <Box
      ref={layoutRef}
      className={cn(LAYOUT_SHELL_CLASS, classNames?.container)}
    >
      {/* `items-start` keeps the content column and rail at their own natural
          heights instead of stretching to match each other, now that neither
          scrolls independently — only the resizer (`self-stretch`) spans the
          full row height. */}
      <Flex className="min-h-0 w-full flex-1 items-start overflow-x-clip">
        <PanelContent
          contentRef={contentRef}
          className={cn(contentClass, classNames?.content)}
        >
          {children}
        </PanelContent>

        {hasAnySection && (
          <>
            <Box className={resizerWrapperClass}>
              <PanelResizer />
            </Box>

            <Flex
              id="ai-panel-sidebar"
              style={railStyle}
              className={cn(
                'rounded-lg overflow-x-hidden',
                railClass,
                classNames?.panel,
              )}
            >
              {shouldRenderAIChat && (
                <Box
                  ref={aiSectionRef}
                  data-testid="ai-section"
                  tabIndex={-1}
                  aria-label={labels.ai}
                  style={aiSectionStyle}
                  className={cn('outline-none', aiSectionClass)}
                >
                  {/* AIPanel ships its own header/Hide button. On desktop Hide
                      collapses the panel (default `closePanel`); on mobile it
                      closes the section overlay instead. */}
                  <AIPanel
                    collapsible={keepSidebarOpen && md && !hasOnlyChat}
                    onHide={md ? undefined : closeActiveSection}
                    fitContainer={!md}
                    trialCta={loggedOutTrialCta}
                  />
                </Box>
              )}
              {hasRelated && (
                <Box
                  ref={relatedSectionRef}
                  data-testid="related-section"
                  tabIndex={-1}
                  aria-label={labels.related}
                  className={cn('outline-none', relatedSectionClass)}
                >
                  <MobileOverlayHeader
                    title={labels.related}
                    onHide={closeActiveSection}
                    className="md:hidden"
                  />
                  <Box className={relatedContentClass}>{sidebarFooter}</Box>
                </Box>
              )}
            </Flex>
          </>
        )}
      </Flex>

      <MobileSectionNav
        sections={sections}
        activeSection={activeSection}
        onSelect={handleSelectSection}
        className={navClass}
      />
    </Box>
  );
};

/**
 * Mobile sectioned-panel layout. Wraps the body in a ContentSizeProvider bound
 * to the outer layout container so the mobile/desktop switch reads the page
 * width.
 */
const SectionedPanelLayout = (props: WithAIPanelInnerProps) => {
  const layoutRef = useRef<HTMLDivElement>(null);
  return (
    <ContentSizeProvider containerRef={layoutRef}>
      <SectionedPanelLayoutBody {...props} layoutRef={layoutRef} />
    </ContentSizeProvider>
  );
};

/**
 * Inner layout shell — reads context to render content + panel side by side.
 * Separated from the provider so it can consume its own context. Pages that
 * pass a persistent `sidebarFooter` (or opt into `keepSidebarOpen`) use the
 * mobile sectioned-panel layout; every other page keeps the default
 * side-by-side layout. `keepSidebarOpen` is included so a v2 page whose
 * `sidebarFooter` is empty (no related content) still gets the sectioned layout
 * for its AI panel rather than silently falling back to the default one.
 */
const WithAIPanelInner = (props: WithAIPanelInnerProps) => {
  if (props.keepSidebarOpen || props.sidebarFooter) {
    return <SectionedPanelLayout {...props} />;
  }
  return <DefaultPanelLayout {...props} />;
};

/**
 * Wraps any page content area to provide the resizable AI side panel.
 * Pages opt in by wrapping their root content in WithAIPanel.
 */
const WithAIPanel = ({
  children,
  panelOptions = getPanelOptions(AIPanelPage.Default),
  title,
  pageContext,
  answers,
  showExamplePrompts = false,
  showLoggedOut = true,
  loggedOutTrialCta,
  classNames,
  sidebarFooter,
  keepSidebarOpen = false,
  matchAIHeightToContent = false,
  hideSidebar = false,
}: Props) => {
  const initialPageContext = useMemo(() => {
    return pageContext || getContextFromAnswers(title || '', answers);
  }, [title, pageContext, answers]);

  return (
    <AIPanelProvider
      panelOptions={panelOptions}
      initialPageContext={initialPageContext}
      topic={title}
      showExamplePrompts={showExamplePrompts}
      showLoggedOut={showLoggedOut}
      keepSidebarOpen={keepSidebarOpen}
      disabled={hideSidebar}
    >
      <AIPanelHeightSyncProvider enabled={matchAIHeightToContent}>
        <WithAIPanelInner
          classNames={classNames}
          sidebarFooter={sidebarFooter}
          keepSidebarOpen={keepSidebarOpen}
          loggedOutTrialCta={loggedOutTrialCta}
          hideSidebar={hideSidebar}
        >
          {children}
        </WithAIPanelInner>
      </AIPanelHeightSyncProvider>
    </AIPanelProvider>
  );
};

export default WithAIPanel;
